import { neueVersionGefunden } from './pwaUpdate';

// Die ServiceWorker-Anmeldung — von Hand, nicht über den Helfer von
// vite-plugin-pwa.
//
// Warum der Umbau (Befunde K2). Der Helfer `registerSW` aus
// `virtual:pwa-register` bringt zwei Verhaltensweisen mit, die auf einem
// Ladengerät stören. Beide stehen in seinem Quelltext
// (node_modules/vite-plugin-pwa/dist/client/build/register.js):
//
//   1. Der Rückruf `updateServiceWorker(reloadPage)` NIMMT den Parameter
//      entgegen und benutzt ihn nicht — er heisst dort `_reloadPage`.
//      Geladen wird stattdessen aus einem Zuhörer auf `controlling`:
//
//          wb.addEventListener('controlling', (event) => {
//            if (event.isUpdate) window.location.reload()
//          })
//
//      Diese Bedingung ist der Grund für „erst der zweite Tipp macht
//      Ruhe". Startet die Seite ohne Controller — bei der installierten
//      App der Normalfall nach einem Kaltstart —, meldet workbox-window
//      `isUpdate: false`. Der neue Worker übernimmt still, geladen wird
//      nichts, und für den Nutzer ist der Knopf kaputt.
//
//   2. Derselbe `controlling`-Zuhörer wird INNERHALB der Melde-Funktion
//      angemeldet, also bei jedem `waiting`-Ereignis erneut. Sie hängen
//      sich auf.
//
// Hier stattdessen: ein wartender Worker wird nur gemeldet, wenn die
// Seite schon von einem Worker kontrolliert wird — sonst ist es die
// Erstinstallation und keine neue Fassung. Und der Wechsel läuft
// ausdrücklich über SKIP_WAITING, danach über `controllerchange`.

/**
 * Wo der erzeugte Worker liegt.
 *
 * vite-plugin-pwa legt ihn im Modus `generateSW` als /sw.js ab. Der Wert
 * hängt an der PWA-Konfiguration in vite.config.ts — wer dort `filename`
 * setzt, muss ihn hier mitziehen.
 */
const SW_PFAD = '/sw.js';

/** Wie oft im Hintergrund nach einer neuen Fassung gesehen wird. */
const UPDATE_INTERVALL_MS = 30 * 60_000;

/**
 * Wie lange nach dem SKIP_WAITING auf die Übernahme gewartet wird, bevor
 * hart neu geladen wird.
 *
 * Der Regelfall ist `controllerchange`. Bleibt es aus — der wartende
 * Worker ist zwischenzeitlich weggeräumt worden, die Nachricht ging ins
 * Leere —, darf der Knopf trotzdem nicht wirkungslos bleiben.
 */
const UEBERNAHME_FRIST_MS = 3_000;

let wechselLaeuft = false;

function wechseln(registrierung: ServiceWorkerRegistration): void {
  // Doppelte Tipps sollen nicht zwei Neuladungen auslösen.
  if (wechselLaeuft) return;
  wechselLaeuft = true;

  const neuladen = (): void => {
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener('controllerchange', neuladen, { once: true });
  window.setTimeout(neuladen, UEBERNAHME_FRIST_MS);

  const wartender = registrierung.waiting;
  if (wartender) {
    wartender.postMessage({ type: 'SKIP_WAITING' });
    return;
  }
  // Kein wartender Worker mehr: dann ist die neue Fassung schon aktiv und
  // ein Neuladen holt sie. Die Frist oben erledigt das.
}

export function serviceWorkerAnmelden(): void {
  if (!('serviceWorker' in navigator)) return;

  void navigator.serviceWorker
    .register(SW_PFAD, { scope: '/' })
    .then((registrierung) => {
      /**
       * Meldet eine wartende Fassung — aber nur, wenn es wirklich eine
       * NEUE ist.
       *
       * `navigator.serviceWorker.controller` ist die Unterscheidung:
       * fehlt er, kontrolliert noch gar kein Worker diese Seite, und was
       * da wartet, ist die Erstinstallation. Dafür einen Hinweis „neue
       * Version verfügbar" zu zeigen, wäre schlicht gelogen.
       */
      const meldenWennWartend = (): void => {
        if (registrierung.waiting === null) return;
        if (navigator.serviceWorker.controller === null) return;
        neueVersionGefunden(() => wechseln(registrierung));
      };

      // Beim Start: hat ein früherer Besuch schon eine Fassung
      // hinterlegt, die noch niemand übernommen hat?
      meldenWennWartend();

      registrierung.addEventListener('updatefound', () => {
        const kommend = registrierung.installing;
        if (kommend === null) return;
        kommend.addEventListener('statechange', () => {
          if (kommend.state === 'installed') meldenWennWartend();
        });
      });

      // Zwei Auslöser, weil einer allein Lücken lässt: der Takt greift bei
      // einem Gerät, das offen daliegt; das Sichtbarwerden greift, wenn
      // jemand die App nach Stunden wieder hervorholt — Hintergrund-
      // Zeitgeber hält iOS-Safari nicht zuverlässig am Leben.
      //
      // Findet eine Prüfung nichts Neues, passiert nichts: ohne neue
      // Fassung gibt es kein `updatefound`, also auch keinen Hinweis.
      window.setInterval(() => {
        void registrierung.update();
      }, UPDATE_INTERVALL_MS);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void registrierung.update();
      });
    })
    .catch((fehler: unknown) => {
      // Ohne Worker läuft die Anwendung weiter, nur ohne Zwischenspeicher.
      console.warn('[PWA] ServiceWorker nicht angemeldet:', fehler);
    });
}
